import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, TouchableOpacity, ActivityIndicator,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import QRCode from 'react-native-qrcode-svg';
import { CameraView } from 'expo-camera';
import NfcManager, { NfcTech } from 'react-native-nfc-manager';
import Style from '../../constants/Style';
import Colors from '../../constants/Colors';
import ScanIcon from '../ScanIcon';
import Button from '../Button';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import CloseModalIcon from '../CloseModalIcon';
import addSolveTag from '../../requests/Assets/AddSolveTag';
import SuccessMessage from '../SuccessMessage';
import { ERROR_ADDING_SOLVE_TAG } from '../../constants/AlertMessages';
import PressableStyled from '../PressableStyled';
import NfcIcon from '../30pxIcons/NfcIcon';
import QrCodeIcon from '../30pxIcons/QrCodeIcon';

interface AddQRCodeModalProps {
  addQRCodeModalShown: boolean,
  hideQRCodeModal: () => void,
  assetId: string,
  addTag,
  tags: string[],
}

const AddQRCodeModal = ({
  addQRCodeModalShown, hideQRCodeModal, assetId, addTag, tags,
}: AddQRCodeModalProps) => {
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const [scannerOpen, setScannerOpen] = useState<boolean>(false);
  const [code, setCode] = useState<string>(null);
  const [adding, setAdding] = useState<boolean>(false);
  const [added, setAdded] = useState<boolean>(false);
  const [addError, setAddError] = useState<string>('');
  const [nfcPressed, setNfcPressed] = useState<boolean>(false);

  const { top } = useSafeAreaInsets();

  useEffect(() => {
    if (addQRCodeModalShown) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    }
  }, [addQRCodeModalShown]);

  useEffect(() => {
    if (code) addTagToAsset();
  }, [code]);

  const closeModal = () => (visibilityToggleEnabled ? hideQRCodeModal() : null);

  const decodePayload = (payload: number[]): string => {
    const filteredPayload = payload.slice(1);
    return String.fromCharCode(...filteredPayload);
  };

  const restoreDefaults: () => void = () => {
    setVisibilityToggleEnabled(true);
    setScannerOpen(false);
    setCode(null);
    setAdding(false);
    setAdded(false);
    setAddError('');
  };

  const addTagToAsset: () => void = () => {
    if (tags?.includes(code)) {
      setAddError('This Solve tag is already added to the asset');
      return;
    }
    setAdding(true);
    setAddError('');
    addSolveTag(assetId, code).then((res) => {
      if (res && !res.isError) {
        addTag(code);
        setAdded(true);
      } else setAddError(res?.errorText ?? ERROR_ADDING_SOLVE_TAG);
    }).catch((e) => {
      setAddError(e?.message ?? ERROR_ADDING_SOLVE_TAG);
    }).finally(() => setAdding(false));
  };

  const onBarCodeScanned = ({ data }: { data: string }) => {
    let scanInput: string | null = null;
    try {
      const parsedUrl = new URL(data);
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
      scanInput = pathSegments[pathSegments.length - 1] ?? null;
    } catch (error) {
      const pathWithoutQuery = data?.split('?')[0]?.split('#')[0] ?? '';
      const pathSegments = pathWithoutQuery.split('/').filter(Boolean);
      scanInput = pathSegments[pathSegments.length - 1] ?? null;
    }
    if (scanInput) setCode(scanInput);
  };

  return <Modal
    style={[Style.modal, styles.modal]}
    isVisible={addQRCodeModalShown}
    onBackdropPress={closeModal}
    onBackButtonPress={hideQRCodeModal}
    onSwipeComplete={hideQRCodeModal}
    swipeDirection='down'
    animationIn='slideInUp'
    animationOut='slideOutDown'
    onModalHide={restoreDefaults}
  >
    <View style={styles.container}>
      <Text style={styles.title}>{'Scan Solve tag'}</Text>
      {added ? <SuccessMessage
        title='Solve tag added to asset successfully!'
        style={styles.successMessage}
      /> : <>
        {code ? <QRCode
          value={code}
          size={50}
        /> : <ScanIcon color={Colors.light.hereworksDarkBlue} />}
        <Text style={styles.subtitle}>
          {code ? `${addError.length > 0 ? addError : `Adding Solve tag: ${code}`}` : 'Scan the Solve tag you want to add this asset'}
        </Text>
        {code ? <Button
          onPress={addTagToAsset}
          affirmative
          style={styles.button}
          disabled={adding}
          children={adding ? <ActivityIndicator
            size='small'
            color='white'
            style={styles.loading}
          /> : null}
          title={adding ? 'Adding Solve tag' : `${addError.length > 0 ? 'Retry' : 'Add Solve tag'}`}
        /> : <View style={Style.row}>
          <PressableStyled
            style={[styles.scanButton, { marginRight: 6 }]}
            onPress={async () => {
              setNfcPressed(true);
              try {
                await NfcManager.start();
                await NfcManager.requestTechnology(NfcTech.Ndef);
                const tag = await NfcManager.getTag();
                NfcManager.cancelTechnologyRequest();
                const decodedTag = decodePayload(tag.ndefMessage[0].payload);
                if (decodedTag) onBarCodeScanned({ data: decodedTag });
                // handleBarCodeScanned({ data: 'https://dev.hereworks.tech/asset00492?ss=QR' });
              } finally {
                setNfcPressed(false);
                NfcManager.cancelTechnologyRequest();
              }
            }}
          >
            <NfcIcon />
            <Text style={styles.scanButtonText}>
              {'Scan NFC tag'}
            </Text>
          </PressableStyled>
          <PressableStyled
            style={[styles.scanButton, { marginLeft: 6 }]}
            onPress={() => {
              setScannerOpen(true);
              // onBarCodeScanned({ data: 'https://dev.hereworks.tech/asset00492?ss=QR' });
            }}
          >
            <QrCodeIcon />
            <Text style={styles.scanButtonText}>
              {'Scan QR code'}
            </Text>
          </PressableStyled>
        </View>}
        {nfcPressed ? <Text style={styles.scanSubtitle}>Approach tag</Text> : null}
        {nfcPressed ? <Button style={{ flex: undefined, alignSelf: 'stretch', marginTop: 8 }} title='Cancel NFC Scan' onPress={() => NfcManager.cancelTechnologyRequest()} /> : null}
      </>}
      <TouchableOpacity
        style={styles.closeButton}
        onPress={hideQRCodeModal}
      >
        <DeleteIcon24 color={Colors.light.hereworksBlack600} />
      </TouchableOpacity>
    </View>
    {scannerOpen && !code && <>
      <CameraView
        onBarcodeScanned={onBarCodeScanned}
        style={StyleSheet.absoluteFill}
      />
      <TouchableOpacity
        style={[Style.closeButton, { top: Math.max(Style.closeButton.top, top) }]}
        onPress={() => setScannerOpen(false)}
      >
        <CloseModalIcon />
      </TouchableOpacity>
    </>}
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: '#FFF',
    borderTopStartRadius: 12,
    borderTopEndRadius: 12,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 32,
    alignItems: 'center',
  },
  title: {
    fontSize: 14,
    lineHeight: 17.5,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    marginBottom: 71,
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginTop: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  button: {
    alignSelf: 'stretch',
    flex: undefined,
    marginStart: undefined,
    flexDirection: 'row-reverse',
  },
  loading: {
    marginEnd: 4,
  },
  closeButton: {
    position: 'absolute',
    end: 24,
    top: 12,
  },
  successMessage: {
    paddingTop: undefined,
  },
  scanButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    marginTop: 12,
    borderColor: Colors.light.hereworksBlue500,
    alignSelf: 'stretch',
    alignItems: 'center',
    paddingVertical: 16,
  },
  scanButtonText: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
    textAlign: 'center',
  },
  scanSubtitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    lineHeight: 20,
    marginTop: 20,
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
  },
});

export default AddQRCodeModal;
